import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import Home from "../views/AppHome.vue";

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: "Home",
        component: Home,
    },
    {
        path: "/support",
        name: "Support",
        component: () =>
            import(/* webpackChunkName: "support" */ "../views/support/SupportQueue.vue"),
    },
    {
        path: "/support/reports",
        name: "SupportReports",
        component: () =>
            import(/* webpackChunkName: "support" */ "../views/support/SupportReports.vue"),
    },
    {
        path: "/auth",
        component: () => import(/* webpackChunkName: "auth" */ "../views/AuthView.vue"),
        redirect: { name: "AuthLogin" },
        meta: {
            guest: true,
        },
        children: [
            {
                name: "AuthLogin",
                path: "login",
                component: () =>
                    import(/* webpackChunkName: "auth" */ "../views/auth/AuthLogin.vue"),
            },
            {
                name: "AuthWelcome",
                path: "welcome",
                props: (route) => ({
                    user: route.query["user"],
                    token: route.query["token"],
                    userName:
                        typeof route.query["userName"] === "string"
                            ? route.query["userName"]
                            : undefined,
                }),
                component: () =>
                    import(/* webpackChunkName: "auth" */ "../views/auth/AuthWelcome.vue"),
            },
            {
                name: "AuthForgot",
                path: "forgot",
                component: () =>
                    import(/* webpackChunkName: "auth" */ "../views/auth/AuthForgot.vue"),
            },
            {
                name: "AuthReset",
                path: "reset",
                props: (route) => ({ user: route.query["user"], token: route.query["token"] }),
                component: () =>
                    import(/* webpackChunkName: "auth" */ "../views/auth/AuthReset.vue"),
            },
        ],
    },
    {
        name: "PortalPicker",
        path: "/portal",
        meta: {
            portal: true,
        },
        component: () => import(/* webpackChunkName: "portal" */ "../portal/PortalPicker.vue"),
    },
    {
        path: "/portal/:slug",
        props: true,
        component: () => import(/* webpackChunkName: "portal" */ "../portal/PortalLayout.vue"),
        redirect: { name: "PortalRequests" },
        meta: {
            portal: true,
        },
        children: [
            {
                name: "PortalLogin",
                path: "login",
                meta: { portalGuest: true },
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalLogin.vue"),
            },
            {
                name: "PortalWelcome",
                path: "welcome",
                meta: { portalGuest: true },
                props: (route) => ({ user: route.query["user"], token: route.query["token"] }),
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalWelcome.vue"),
            },
            {
                name: "PortalRegister",
                path: "register",
                meta: { portalGuest: true },
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalRegister.vue"),
            },
            {
                name: "PortalForgot",
                path: "forgot",
                meta: { portalGuest: true },
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalForgot.vue"),
            },
            {
                name: "PortalReset",
                path: "reset",
                meta: { portalGuest: true },
                props: (route) => ({ user: route.query["user"], token: route.query["token"] }),
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalReset.vue"),
            },
            {
                name: "PortalSurvey",
                path: "survey/:surveyId",
                // Public: works from the email link whether or not a session exists.
                meta: { portalPublic: true },
                props: (route) => ({
                    surveyId: route.params["surveyId"],
                    score: route.query["score"],
                }),
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalSurvey.vue"),
            },
            {
                name: "PortalRequests",
                path: "requests",
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalRequests.vue"),
            },
            {
                name: "PortalRequestDetail",
                path: "requests/:requestId",
                props: true,
                component: () =>
                    import(
                        /* webpackChunkName: "portal" */ "../portal/views/PortalRequestDetail.vue"
                    ),
            },
            {
                name: "PortalAccount",
                path: "account",
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalAccount.vue"),
            },
            {
                name: "PortalCompany",
                path: "company",
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalCompany.vue"),
            },
            {
                name: "PortalNewRequest",
                path: "new/:projectKey?",
                props: true,
                component: () =>
                    import(/* webpackChunkName: "portal" */ "../portal/views/PortalNewRequest.vue"),
            },
        ],
    },
    {
        name: "TicketSearch",
        path: "/tickets/search/:ticketId?/:table?/:list?",
        props: true,
        component: () =>
            import(/* webpackChunkName: "tickets" */ "../views/tickets/TicketSearch.vue"),
    },
    {
        name: "CreateTicket",
        path: "/tickets/create",
        props: true,
        component: () =>
            import(/* webpackChunkName: "tickets" */ "@/views/tickets/TicketCreate.vue"),
    },
    {
        name: "TicketView",
        path: "/tickets/:ticketId",
        props: true,
        component: () =>
            import(/* webpackChunkName: "tickets" */ "../views/tickets/TicketView.vue"),
    },
    {
        name: "MyQueue",
        path: "/queue",
        component: () => import(/* webpackChunkName: "queue" */ "../views/MyQueueView.vue"),
    },
    {
        name: "MyTime",
        path: "/time",
        component: () => import(/* webpackChunkName: "time" */ "../views/MyTimeView.vue"),
    },
    {
        name: "TicketLists",
        path: "/lists/:listId?/:ticketId?",
        props: true,
        component: () =>
            import(/* webpackChunkName: "tickets" */ "../views/tickets/TicketList.vue"),
    },
    {
        name: "Admin",
        path: "/admin",
        component: () => import(/* webpackChunkName: "admin" */ "../views/AdminHome.vue"),
        children: [
            {
                name: "AdminDashboard",
                path: "",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/AdminDashboard.vue"),
            },
            {
                name: "OrganizationList",
                path: "organizations",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/OrganizationList.vue"),
            },
            {
                name: "UserList",
                path: "users",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/UserList.vue"),
            },
            {
                name: "Portals",
                path: "portals",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/PortalsAdmin.vue"),
            },
            {
                name: "Configs",
                path: "configs/:tab?",
                props: true,
                beforeEnter: (to) =>
                    !to.params["tab"]
                        ? { name: to.name!, params: { ...to.params, tab: "project configuration" } }
                        : undefined,
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/ConfigurationsList.vue"),
            },
            {
                name: "System Settings",
                path: "settings",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/SystemSettings.vue"),
            },
            {
                name: "Support Calendars",
                path: "support-calendars",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/SupportCalendars.vue"),
            },
            {
                name: "SLA Policies",
                path: "sla-policies",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/SlaPolicies.vue"),
            },
            {
                name: "Audit Logs",
                path: "audit",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/AuditLogs.vue"),
            },
            {
                name: "Import Data",
                path: "import",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/ImportData.vue"),
            },
            {
                name: "JiraSync",
                path: "jira-sync",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/JiraSync.vue"),
            },
            {
                name: "PagerDuty",
                path: "pagerduty",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/PagerDutyAdmin.vue"),
            },
            {
                name: "Maintenance",
                path: "maintenance",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/MaintenanceTasks.vue"),
            },
            {
                name: "TimeReview",
                path: "time-review",
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/TimeReview.vue"),
            },
            {
                name: "Rates",
                path: "rates/:tab?",
                props: true,
                beforeEnter: (to) =>
                    !to.params["tab"]
                        ? { name: to.name!, params: { ...to.params, tab: "people" } }
                        : undefined,
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/RatesView.vue"),
            },
            {
                name: "Recovery",
                path: "recovery/:tab?",
                props: true,
                beforeEnter: (to) =>
                    !to.params["tab"]
                        ? { name: to.name!, params: { ...to.params, tab: "tickets" } }
                        : undefined,
                component: () =>
                    import(/* webpackChunkName: "admin" */ "../views/admin/SoftDeleteRecovery.vue"),
            },
        ],
    },
    {
        name: "OrganizationView",
        path: "/organizations/:organizationId/:tab?",
        props: true,
        beforeEnter: (to) =>
            !to.params["tab"]
                ? { name: to.name!, params: { ...to.params, tab: "projects" } }
                : undefined,
        component: () =>
            import(
                /* webpackChunkName: "organizations" */ "../views/organizations/OrganizationView.vue"
            ),
    },
    {
        name: "BudgetView",
        path: "/budgets/:budgetId",
        props: true,
        component: () =>
            import(/* webpackChunkName: "organizations" */ "../views/budgets/BudgetView.vue"),
    },
    {
        name: "TeamView",
        path: "/teams/:teamId/:tab?",
        props: true,
        beforeEnter: (to) =>
            !to.params["tab"]
                ? { name: to.name!, params: { ...to.params, tab: "users" } }
                : undefined,
        component: () =>
            import(/* webpackChunkName: "organizations" */ "../views/organizations/TeamView.vue"),
    },
    {
        name: "ProjectList",
        path: "/projects",
        component: () =>
            import(/* webpackChunkName: "projects" */ "../views/projects/ProjectList.vue"),
    },
    {
        name: "ProjectView",
        path: "/projects/:projectId/:tab?",
        props: true,
        beforeEnter: (to) =>
            !to.params["tab"]
                ? { name: to.name!, params: { ...to.params, tab: "overview" } }
                : undefined,
        component: () =>
            import(/* webpackChunkName: "projects" */ "../views/projects/ProjectView.vue"),
    },
    {
        name: "UserView",
        path: "/users/:userId",
        props: true,
        component: () => import(/* webpackChunkName: "users" */ "../views/users/UserView.vue"),
    },
    {
        name: "Settings",
        path: "/settings/:tab?",
        props: true,
        beforeEnter: (to) =>
            !to.params["tab"]
                ? { name: to.name!, params: { ...to.params, tab: "profile" } }
                : undefined,
        component: () =>
            import(/* webpackChunkName: "settings" */ "../views/settings/SettingsHome.vue"),
    },
];

const router = createRouter({
    history: createWebHistory(process.env["BASE_URL"]),
    routes,
});

export default router;
